const API_BASE_URL = String(process.env.VUE_APP_API_BASE_URL || '/api').replace(/\/$/, '')
const PRODUCT_PLACEHOLDER = '/product-placeholder.png'

function toNumber(value, fallback = 0) {
  const numberValue = Number(value)
  return Number.isFinite(numberValue) ? numberValue : fallback
}

function normalizeQuantity(value, min = 1, max = 2147483647) {
  const lower = Math.min(2147483647, Math.max(1, Math.trunc(toNumber(min, 1))))
  const upper = Math.max(lower, Math.min(2147483647, Math.trunc(toNumber(max, 2147483647))))
  const raw = String(value == null ? '' : value).trim()
  const count = /^\d+$/.test(raw) ? Number(raw) : lower
  return Math.min(upper, Math.max(lower, Number.isFinite(count) ? count : upper))
}

function formatMoney(value, digits = 3) {
  return toNumber(value, 0).toFixed(digits)
}

function getTierPrice(item, quantity) {
  const tiers = Array.isArray(item && item.priceTiers) ? item.priceTiers : []
  const count = Math.max(1, toNumber(quantity, item && item.quantity ? item.quantity : 1))
  const matched = tiers
    .filter(tier => toNumber(tier.minQuantity, 1) <= count)
    .sort((a, b) => toNumber(b.minQuantity, 1) - toNumber(a.minQuantity, 1))[0]
  return matched ? toNumber(matched.price, item && item.price ? item.price : 0) : toNumber(item && item.price ? item.price : 0)
}

function normalizeAssetPath(path) {
  if (!path || !String(path).trim()) return PRODUCT_PLACEHOLDER
  const value = String(path).trim()
  if (/(?:^|\/)(?:fff|product-placeholder)\.png(?:[?#].*)?$/i.test(value)) return PRODUCT_PLACEHOLDER
  // 历史绝对地址和相对地址统一改走 /api，确保生产反向代理能把请求交给 Java 服务。
  const uploadIndex = value.indexOf('/uploads/')
  if (uploadIndex >= 0) return API_BASE_URL + value.slice(uploadIndex)
  if (value.indexOf('uploads/') === 0) return API_BASE_URL + '/' + value
  if (/^https?:\/\//.test(value)) return value
  if (value.indexOf('/static/file/') === 0) return value.replace('/static/file/', '/')
  if (value.indexOf('static/file/') === 0) return value.replace('static/file/', '/')
  if (value.indexOf('file/') === 0) return value.replace('file/', '/')
  if (value.charAt(0) === '/') return value
  return `/${value}`
}

function normalizeAssetList(value, fallback = [PRODUCT_PLACEHOLDER]) {
  let images = []
  if (Array.isArray(value)) {
    images = value
  } else if (value && String(value).trim().charAt(0) === '[') {
    try {
      const parsed = JSON.parse(value)
      images = Array.isArray(parsed) ? parsed : []
    } catch (e) {
      images = []
    }
  } else if (value) {
    images = [value]
  }
  const normalized = images.filter(item => typeof item === 'string' && item.trim()).map(normalizeAssetPath)
  return normalized.length ? normalized : fallback
}

function normalizePosterImages(value) {
  if (Array.isArray(value)) {
    return normalizeAssetList(value, [])
  }
  try {
    const parsed = JSON.parse(value || '[]')
    return Array.isArray(parsed) ? normalizeAssetList(parsed, []) : []
  } catch (e) {
    return []
  }
}

function normalizePriceTiers(value) {
  const tiers = Array.isArray(value) ? value : []
  return tiers
    .filter(item => item && item.price !== null && item.price !== undefined && String(item.price).trim() !== '')
    .map(item => ({
      minQuantity: Math.max(1, toNumber(item.minQuantity, 1)),
      price: formatMoney(item.price, 4)
    }))
    .sort((a, b) => a.minQuantity - b.minQuantity)
}

function normalizeProduct(item) {
  const product = item || {}
  const images = normalizeProductImages(product)
  const priceTiers = normalizePriceTiers(product.priceTiers || product.prices || [])
  const rawPrice = product.price !== null && product.price !== undefined && String(product.price).trim() !== ''
    ? product.price
    : product.salePrice
  const hasPrice = priceTiers.length > 0 || (rawPrice !== null && rawPrice !== undefined && String(rawPrice).trim() !== '')
  const firstPrice = priceTiers.length ? priceTiers[0].price : (hasPrice ? formatMoney(rawPrice, 4) : null)
  return {
    ...product,
    id: product.id || product.productId || product.model || product.name,
    productId: product.productId || product.id,
    name: product.name || product.model || '未命名商品',
    image: images[0],
    images,
    brand: product.brand || '-',
    package: product.packageName || product.package || '-',
    batch: product.batch || product.batchNo || '',
    desc: product.summary || product.desc || product.name || '-',
    pack: product.standardPackage || product.pack || '-',
    supplier: product.supplier || '锐高在库',
    stock: toNumber(product.stock, 0),
    min: Math.max(1, toNumber(product.minQuantity || product.min, 1)),
    increment: Math.max(1, toNumber(product.increment, 1)),
    minPackage: product.minPackage || product.standardPackage || '-',
    price: firstPrice,
    hasPrice,
    priceTiers
  }
}

function normalizeProductImages(product = {}) {
  const images = [product.coverImage, product.productImages, product.images, product.image]
    .flatMap(value => normalizeAssetList(value, []))
  const actual = images.filter(value => value !== PRODUCT_PLACEHOLDER)
  return actual.length ? [...new Set(actual)] : [PRODUCT_PLACEHOLDER]
}

function handleProductImageError(event) {
  const image = event && event.target
  if (image && image.src && image.src.indexOf(PRODUCT_PLACEHOLDER) < 0) image.src = PRODUCT_PLACEHOLDER
}

function mergeCartItem(items, product, quantity = 1) {
  const list = Array.isArray(items) ? items.slice() : []
  const normalized = normalizeProduct(product)
  const productId = normalized.id || normalized.productId || normalized.name
  const addQuantity = normalizeQuantity(quantity, normalized.min, normalized.stock)
  const exist = list.find(item => String(item.id) === String(productId))

  if (exist) {
    exist.quantity = normalizeQuantity(toNumber(exist.quantity, normalized.min) + addQuantity, normalized.min, normalized.stock)
    exist.checked = true
    return list
  }

  list.unshift({
    id: productId,
    productId,
    name: normalized.name,
    model: normalized.model || normalized.name,
    image: normalized.image,
    images: normalized.images,
    brand: normalized.brand,
    categoryName: normalized.categoryName || normalized.category || '',
    subCategoryName: normalized.subCategoryName || '',
    package: normalized.package,
    desc: normalized.desc,
    pack: normalized.pack,
    supplier: normalized.supplier,
    batch: normalized.batch,
    stock: normalized.stock,
    min: normalized.min,
    increment: normalized.increment,
    price: normalized.price,
    priceTiers: normalized.priceTiers,
    quantity: addQuantity,
    checked: true
  })
  return list
}

function normalizePointsGift(item) {
  const gift = item || {}
  const pointsPrice = Math.max(0, toNumber(gift.pointsPrice || gift.points || gift.price, 0))
  const limitQuantity = Math.max(1, toNumber(gift.limitQuantity, 1))
  return {
    ...gift,
    id: gift.id,
    name: gift.name || '未命名积分商品',
    category: gift.giftCategory || gift.category || '锐高自有',
    giftCategory: gift.giftCategory || gift.category || '锐高自有',
    coverImage: normalizeAssetPath(gift.coverImage || gift.image),
    pointsPrice,
    stock: toNumber(gift.stock, 0),
    description: gift.description || '暂无商品介绍',
    detailHtml: gift.detailHtml || gift.description || '暂无商品详情',
    limitQuantity,
    limitText: `每个用户限兑换${limitQuantity}次`
  }
}

const pointsRanges = {
  不限: [0, Infinity],
  '0-5万': [0, 50000],
  '5万-10万': [50000, 100000],
  '10万-20万': [100000, 200000],
  '20万以上': [200000, Infinity],
  '5-10万': [50000, 100000],
  '10-20万': [100000, 200000]
}

function filterPointsGifts(gifts, options = {}) {
  const category = options.category || '不限'
  const range = options.range || '不限'
  const sortType = options.sortType || 'default'
  const sortOrder = options.sortOrder || 'asc'
  const limits = pointsRanges[range] || pointsRanges['不限']
  const direction = sortOrder === 'desc' ? -1 : 1

  const filtered = (Array.isArray(gifts) ? gifts : [])
    .filter(item => category === '不限' || item.category === category || item.giftCategory === category)
    .filter(item => item.pointsPrice >= limits[0] && item.pointsPrice < limits[1])

  if (sortType !== 'points') return filtered
  return filtered.sort((a, b) => (a.pointsPrice - b.pointsPrice) * direction)
}

function stripHtml(value) {
  return String(value || '').replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim()
}

function normalizePcNews(item) {
  const news = item || {}
  const publishDate = news.publishDate || ''
  const dateText = String(publishDate || '')
  const plainContent = stripHtml(news.contentHtml)
  const summary = news.summary || plainContent.slice(0, 120)
  return {
    ...news,
    id: news.id,
    title: news.title || '未命名新闻',
    summary,
    coverImage: normalizeAssetPath(news.coverImage),
    source: news.source || '锐高',
    publishDate: dateText,
    monthDay: dateText.length >= 10 ? dateText.slice(5, 10) : '',
    year: dateText.length >= 4 ? dateText.slice(0, 4) : '',
    contentHtml: (news.contentHtml || `<p>${summary || '暂无新闻内容'}</p>`).replace(/(<img\b[^>]*\bsrc=["'])([^"']+)(["'])/gi,
      (match, prefix, src, suffix) => prefix + (/^(?:data:image\/|blob:)/i.test(src) ? src : normalizeAssetPath(src)) + suffix),
    sortOrder: toNumber(news.sortOrder, 0),
    isTop: toNumber(news.isTop, 0),
    viewCount: toNumber(news.viewCount, 0),
    status: news.status == null ? 1 : toNumber(news.status, 1)
  }
}

function normalizePcVideo(item) {
  const video = item || {}
  return {
    ...video,
    id: video.id,
    title: video.title || '未命名视频',
    summary: video.summary || '暂无视频介绍',
    coverImage: normalizeAssetPath(video.coverImage || video.image),
    videoUrl: video.videoUrl || video.url || '',
    source: video.source || '锐高',
    sortOrder: toNumber(video.sortOrder, 0),
    isTop: toNumber(video.isTop, 0),
    viewCount: toNumber(video.viewCount, 0),
    status: video.status == null ? 1 : toNumber(video.status, 1)
  }
}

function filterPcNewsList(list, keyword) {
  const text = String(keyword || '').trim().toLowerCase()
  const newsList = Array.isArray(list) ? list : []
  if (!text) return newsList
  return newsList.filter(item => {
    return [item.title, item.summary, item.source].some(value => String(value || '').toLowerCase().indexOf(text) > -1)
  })
}

export {
  normalizeProductImages,
  normalizeQuantity,
  toNumber,
  formatMoney,
  getTierPrice,
  normalizeAssetPath,
  normalizeAssetList,
  normalizePosterImages,
  normalizePriceTiers,
  normalizeProduct,
  PRODUCT_PLACEHOLDER,
  handleProductImageError,
  normalizePointsGift,
  filterPointsGifts,
  normalizePcNews,
  normalizePcVideo,
  filterPcNewsList,
  mergeCartItem
}

export default {
  normalizeProductImages,
  normalizeQuantity,
  toNumber,
  formatMoney,
  getTierPrice,
  normalizeAssetPath,
  normalizeAssetList,
  normalizePosterImages,
  normalizePriceTiers,
  normalizeProduct,
  PRODUCT_PLACEHOLDER,
  handleProductImageError,
  normalizePointsGift,
  filterPointsGifts,
  normalizePcNews,
  normalizePcVideo,
  filterPcNewsList,
  mergeCartItem
}
